import React, { useEffect } from "react";

/**
 * Modal base del sistema de UI (para diálogos con contenido propio,
 * distinto de ModalConfirmar que es para confirmaciones simples).
 * Uso:
 *   <Modal show={show} onClose={() => setShow(false)} title="Editar producto">
 *     <Modal.Body>...</Modal.Body>
 *     <Modal.Footer>...</Modal.Footer>
 *   </Modal>
 *
 * Props:
 *  - show: controla la visibilidad
 *  - onClose: se llama al pulsar la X, el fondo o Escape
 *  - title: título en la cabecera (opcional si se usa contenido custom)
 *  - size: "sm" | "md" | "lg" | "xl"
 *  - closeOnBackdrop: cerrar al hacer clic fuera del contenido (default true)
 */
function Modal({
  show,
  onClose,
  title,
  size = "md",
  closeOnBackdrop = true,
  className = "",
  children,
}) {
  useEffect(() => {
    if (!show) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block sw-modal-overlay"
      tabIndex="-1"
      role="dialog"
      onClick={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose?.();
      }}
      style={{ zIndex: 9998 }}
    >
      <div className={`modal-dialog modal-dialog-centered sw-ui-modal--${size}`}>
        <div className={`modal-content ${className}`}>
          {title && (
            <div className="modal-header">
              <h5 className="modal-title fw-bold">{title}</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                aria-label="Cerrar"
                style={{ filter: "invert(1) brightness(0.7)" }}
              />
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

Modal.Body = function ModalBody({ className = "", children, ...rest }) {
  return (
    <div className={`modal-body ${className}`} {...rest}>
      {children}
    </div>
  );
};

Modal.Footer = function ModalFooter({ className = "", children, ...rest }) {
  return (
    <div className={`modal-footer ${className}`} {...rest}>
      {children}
    </div>
  );
};

export default Modal;
